import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { attachAdminAuth } from "./admin-auth-attacher";
import { SOURCES, type AnalyticsEvent } from "./analytics-catalog";
import { audience, sessionsOf, summarize, orderedFunnel, FUNNELS } from "./analytics-math";
import { setResponseHeader } from "@tanstack/react-start/server";

const schema = z.object({
  days: z.union([z.literal(7),z.literal(30),z.literal(90),z.literal(365)]).default(30),
  start: z.string().date().optional(), end: z.string().date().optional(),
  source: z.enum(SOURCES).optional(), device: z.enum(["mobile","tablet","desktop"]).optional(),
  lang: z.enum(["nl","fr","en"]).optional(), packageId: z.string().max(80).optional(), bots: z.boolean().default(false),
});
export type AnalyticsFilters = z.input<typeof schema>;
export const getAnalyticsStats = createServerFn({ method: "GET" })
  .middleware([attachAdminAuth, requireSupabaseAuth])
  .inputValidator((input: unknown) => schema.parse(input))
  .handler(async ({ data: input, context }) => {
    setResponseHeader("Cache-Control", "no-store");
    const { data: roles, error: roleError } = await context.supabase.from("user_roles").select("role").eq("user_id",context.userId).eq("role","admin");
    if (roleError || !roles?.length) throw new Error("Access denied");
    const end = input.end ? new Date(`${input.end}T00:00:00Z`).getTime()+86400000 : Date.now();
    const start = input.start ? new Date(`${input.start}T00:00:00Z`).getTime() : end-input.days*86400000;
    if (start >= end || end-start > 366*86400000 || end > Date.now()+86400000) throw new Error("Invalid period");
    const previousStart = start-(end-start);
    const rows: AnalyticsEvent[] = [];
    // Stable pagination, no silent 20,000 row cap.
    for (let offset=0; ; offset+=1000) {
      const result = await context.supabase.from("analytics_events").select("id,occurred_at,event,page,lang,session_id,source,device,is_bot,interacted,props")
        .gte("occurred_at",new Date(previousStart).toISOString()).lt("occurred_at",new Date(end).toISOString())
        .order("occurred_at").order("id").range(offset,offset+999);
      if (result.error) throw new Error("Analytics unavailable");
      rows.push(...(result.data ?? []) as AnalyticsEvent[]);
      if (rows.length > 200000) throw new Error("Selected period contains too many events; choose a shorter period");
      if ((result.data?.length ?? 0) < 1000) break;
    }
    const allSessions = sessionsOf(rows);
    const matching = [...allSessions.values()].filter(events => {
      const first = events[0];
      return first && (input.bots || audience(events)==="human") && (!input.source || first.source===input.source)
        && (!input.device || first.device===input.device) && (!input.lang || events.some(e=>e.lang===input.lang))
        && (!input.packageId || events.some(e=>e.props.package_id===input.packageId));
    }).flat();
    const current = matching.filter(e=>new Date(e.occurred_at).getTime()>=start);
    const previous = matching.filter(e=>new Date(e.occurred_at).getTime()<start);
    const audiences = { human:0, bot:0, suspect:0 };
    for (const events of sessionsOf(rows.filter(e=>new Date(e.occurred_at).getTime()>=start)).values()) audiences[audience(events)]++;
    const segments = (["source","device","lang"] as const).flatMap(dimension => {
      const groups = new Map<string, AnalyticsEvent[]>();
      for (const events of sessionsOf(current).values()) {
        const key = events[0]?.[dimension];
        if (key) groups.set(key,[...(groups.get(key) ?? []),...events]);
      }
      return [...groups].map(([value,events])=>({ dimension,value,sessions:sessionsOf(events).size,
        funnels: Object.fromEntries(Object.entries(FUNNELS).map(([key,steps])=>[key,orderedFunnel(events,steps)])) }));
    });
    const first = await context.supabase.from("analytics_events").select("occurred_at").order("occurred_at").limit(1);
    if (first.error) throw new Error("Analytics unavailable");
    const monthly = await context.supabase.from("analytics_monthly").select("month,event,events,sessions").order("month").limit(1000);
    if (monthly.error) throw new Error("Analytics unavailable");
    return { current:summarize(current), previous:summarize(previous), audiences, segments,
      since:first.data?.[0]?.occurred_at ?? null, archiveSince:monthly.data?.[0]?.month ?? null,
      start:new Date(start).toISOString(),end:new Date(end).toISOString(),previousStart:new Date(previousStart).toISOString(),
      packages:[...new Set(rows.map(e=>e.props.package_id).filter((p):p is string=>Boolean(p)))],
    };
  });